import React, { useState, useEffect } from "react";
import SeoHelmet from "../components/SeoHelmet";
import Config from "../Config.json";
import { useNavigate, useParams } from "react-router-dom";
import { getArticleDetails } from "../Fetching";
import { useTranslation } from "react-i18next";

const Learning = () => {
  const TITLE = Config.SITE_TITLE + " | Machine Learning";
  const DESC = "Explore the latest Machine Learning articles and AI innovations.";
  const CANONICAL = Config.SITE_DOMAIN + "/learning";

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { lng } = useParams();
  const language = lng || i18n.language || "en";

  const [filteredArticles, setFilteredArticles] = useState([]);
  const [counters, setCounters] = useState({});

  const calculateElapsedTime = (timeString) => {
    const publicationDate = new Date(timeString);
    const now = new Date();
    const diffInMs = now - publicationDate;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    return `${diffInDays} days ago`;
  };

  const parseTimeToMs = (t) => {
    if (!t) return 0;
    const iso = t.replace(" ", "T");
    const d = new Date(iso);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  };

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const allArticles = await getArticleDetails(language);

        const mlArticles = (allArticles || [])
          .filter((article) => article.category === 2 || article.category === "2")
          .slice()
          .sort((a, b) => parseTimeToMs(b.time) - parseTimeToMs(a.time)); // legfrissebb előre

        setFilteredArticles(mlArticles);

        const initialCounters = {};
        mlArticles.forEach((article) => {
          initialCounters[article.ArtId] = {
            likes: article.plus || 0,
            dislikes: article.minus || 0,
          };
        });
        setCounters(initialCounters);
      } catch (error) {
        console.error("❌ Error fetching Machine Learning articles:", error);
      }
    };

    fetchArticles();
  }, [language]);

  const handleLike = (articleId) => {
    setCounters((prev) => ({
      ...prev,
      [articleId]: {
        likes: (prev[articleId]?.likes || 0) + 1,
        dislikes: prev[articleId]?.dislikes || 0,
      },
    }));
  };

  const handleDislike = (articleId) => {
    setCounters((prev) => ({
      ...prev,
      [articleId]: {
        likes: prev[articleId]?.likes || 0,
        dislikes: (prev[articleId]?.dislikes || 0) + 1,
      },
    }));
  };

  // 🔹 Új URL-alapú navigáció minden cikkhez
  const handleReadMore = (artId) => {
    navigate(`/article/${artId}?lang=${language}`);
  };

  return (
    <main className="main-content">
      <SeoHelmet
        title={TITLE}
        description={DESC}
        keywords={["AI", "Machine Learning", "GPT", "Articles", "AI Tools"]}
        url={CANONICAL}
        lang={language}
        author="Zsolt Fenyő"
      />

      <button id="TopBtn" title="Go to top" style={{ display: "none" }}>
        &#10224;
      </button>

      <div className="container ai-container">
        <div className="row">
          <div className="col-12">
            {/* Statikus tartalom */}
            <div>
              <h2><b>{t("MLTitle")}</b></h2>
              <h3><b>{t("MLh31")}</b></h3>
              <p>{t("MLp1")}</p>
            </div>

            {/* Cikkek megjelenítése */}
            {filteredArticles.length === 0 ? (
              <p className="text-center">No Machine Learning articles available.</p>
            ) : (
              <div className="row g-3">
                {filteredArticles.map((article) => (
                  <div className="col-sm-12 col-md-6 col-lg-3" key={article.ArtId}>
                    <div className="card h-100">
                      <img
                        src={
                          article.img
                            ? `/articleImages/${article.img.split("/").pop()}`
                            : "/placeholder-300x200.png"
                        }
                        className="card-img-top"
                        alt={article.alt || "Article Image"}
                        onError={(e) => {
                          if (!e.target.dataset.errorHandled) {
                            e.target.dataset.errorHandled = "true";
                            e.target.src = "/placeholder-300x200.png";
                          }
                        }}
                      />
                      <div className="card-body">
                        <h5 className="card-title">{article.Title}</h5>
                        <p className="card-text text-truncate">{article.Teaser}</p>
                        <p className="text-muted">
                          Published: {calculateElapsedTime(article.time)}
                        </p>
                        <div className="d-flex justify-content-between">
                          <button
                            className="btn btn-outline-success btn-sm"
                            onClick={() => handleLike(article.ArtId)}
                          >
                            👍 {counters[article.ArtId]?.likes || 0}
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDislike(article.ArtId)}
                          >
                            👎 {counters[article.ArtId]?.dislikes || 0}
                          </button>
                        </div>
                        <button
                          onClick={() => handleReadMore(article.ArtId)}
                          className="btn btn-AIinsideLife-primary w-100 mt-2"
                        >
                          Read More...
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default Learning;
